import React, { useState } from 'react';
import { FileText, AlertTriangle, ShieldCheck, Sparkles, Copy, Check, Bot, Gauge } from 'lucide-react';
import { KeywordResearchProject } from '../../types';
import { Button } from '../UI/Button';
import { Badge } from '../UI/Badge';

interface ContentPlacementWorkstationProps {
  project: KeywordResearchProject;
  masterPrimary: string;
  primaryVariations: string[];
  masterSecondary: string;
  secondaryVariations: string[];
  masterTertiary: string;
  tertiaryVariations: string[];
  onUpdateProject: (updated: KeywordResearchProject) => void;
}

export const ContentPlacementWorkstation: React.FC<ContentPlacementWorkstationProps> = ({
  project,
  masterPrimary,
  primaryVariations,
  masterSecondary,
  secondaryVariations,
  masterTertiary,
  tertiaryVariations,
}) => {
  const [selectedPageKey, setSelectedPageKey] = useState<string>('homepage');
  const [wordCountBaseline, setWordCountBaseline] = useState<500 | 1000>(500);
  const [draftContentMap, setDraftContentMap] = useState<Record<string, string>>({});
  const [copiedPromptId, setCopiedPromptId] = useState<string | null>(null);
  const [showAiDrawer, setShowAiDrawer] = useState<boolean>(false);

  const businessName = project.businessProfile.businessName || 'Business';
  const location = project.businessProfile.confirmedServices[0] || 'Target Region';
  const servicePages = project.servicePageMappings || [];

  // Get active page target details
  let activePageName = 'Homepage (/)';
  let activeTargetUrl = '/';
  let activePrimary1 = masterPrimary;
  let activePrimary2 = primaryVariations[0] || '';
  let activeSecondary1 = masterSecondary;
  let activeSecondary2 = secondaryVariations[0] || '';
  let activeTertiary1 = masterTertiary;
  let activeTertiary2 = tertiaryVariations[0] || '';

  if (selectedPageKey !== 'homepage') {
    const sp = servicePages.find(s => s.id === selectedPageKey);
    if (sp) {
      activePageName = sp.pageName || 'Service Page';
      activeTargetUrl = sp.targetUrl || '/services/page';
      activePrimary1 = sp.primaryKeyword || 'Primary Target';
      activePrimary2 = sp.primaryKeyword2 || '';
      activeSecondary1 = sp.secondaryKeyword || 'Secondary Target';
      activeSecondary2 = sp.secondaryKeyword2 || '';
      activeTertiary1 = sp.tertiaryKeyword || 'Tertiary Target';
      activeTertiary2 = sp.tertiaryKeyword2 || '';
    }
  }

  // Live content stats
  const currentContent = draftContentMap[selectedPageKey] || '';
  const words = currentContent.trim() ? currentContent.trim().split(/\s+/).length : 0;

  const countOccurrences = (text: string, kw: string) => {
    if (!kw || !text) return 0;
    const escaped = kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`\\b${escaped}\\b`, 'gi');
    const matches = text.match(regex);
    return matches ? matches.length : 0;
  };

  const primaryCount = countOccurrences(currentContent, activePrimary1) + (activePrimary2 ? countOccurrences(currentContent, activePrimary2) : 0);
  const secondaryCount = countOccurrences(currentContent, activeSecondary1) + (activeSecondary2 ? countOccurrences(currentContent, activeSecondary2) : 0);
  const tertiaryCount = countOccurrences(currentContent, activeTertiary1) + (activeTertiary2 ? countOccurrences(currentContent, activeTertiary2) : 0);

  const totalKwMatches = primaryCount + secondaryCount + tertiaryCount;
  const keywordDensity = words > 0 ? ((totalKwMatches / words) * 100).toFixed(2) : '0.00';
  const densityNum = parseFloat(keywordDensity);

  // Targets based on baseline
  const primaryTargetCount = wordCountBaseline === 500 ? 3 : 5;
  const secondaryTargetCount = wordCountBaseline === 500 ? 2 : 4;
  const tertiaryTargetCount = wordCountBaseline === 500 ? 1 : 2;

  // Build AI Content Prompt
  const buildAiContentPrompt = () => {
    return `You are a Senior SEO Copywriter generating website page content adhering strictly to the Lecture 11 Keyword Placement & Density Framework.

PAGE & BUSINESS CONTEXT:
- Business Name: ${businessName}
- Target Location / Region: ${location}
- Page Name: ${activePageName}
- Target URL Path: ${activeTargetUrl}
- Content Word Count Target: STRICTLY ${wordCountBaseline} WORDS

KEYWORD TARGETS & VARIATIONS:
- 🥇 Primary Target Keyword: "${activePrimary1}" ${activePrimary2 ? `(Variation: "${activePrimary2}")` : ''}
- 🥈 Secondary Target Keyword: "${activeSecondary1}" ${activeSecondary2 ? `(Variation: "${activeSecondary2}")` : ''}
- 🥉 Tertiary Target Keyword: "${activeTertiary1}" ${activeTertiary2 ? `(Variation: "${activeTertiary2}")` : ''}

STRICT KEYWORD PLACEMENT FORMULA (${wordCountBaseline} WORDS BASELINE):
1. PRIMARY KEYWORD (Use EXACTLY ${primaryTargetCount} times total):
   - Placement 1: 1x in the INTRO / HOOK PARAGRAPH (introducing the business offering).
   - Placement 2: 1x in an H2 SUBHEADING or MIDDLE BODY PARAGRAPH.
   - Placement 3: 1x in the CONCLUSION / ENDING NOTE (final 2-3 lines of the page).

2. SECONDARY KEYWORD (Use EXACTLY ${secondaryTargetCount} times total):
   - Placement 1: 1x in an H2 SUBHEADING (exact or variation).
   - Placement 2: 1x in the BODY TEXT naturally.

3. TERTIARY KEYWORD (Use EXACTLY ${tertiaryTargetCount} time total):
   - Placement 1: 1x in an H3 SUBHEADING or BODY / CONCLUSION paragraph.

KEYWORD DENSITY & ANTI-PENALTY GUARD:
- Target Keyword Density: ~0.5% (Do NOT exceed 1.0%).
- DO NOT keyword stuff. Google penalizes repetitive keyword stuffing.
- Write natural, commercial, human-sounding copy that reads like a real business speaking directly to customers.

OUTPUT FORMAT:
Generate the complete ${wordCountBaseline}-word page content incorporating H1, H2, H3 headings and body paragraphs.`;
  };

  const handleCopyPrompt = () => {
    navigator.clipboard.writeText(buildAiContentPrompt());
    setCopiedPromptId(selectedPageKey);
    setTimeout(() => setCopiedPromptId(null), 2500);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      
      {/* 1. TOP HEADER BANNER */}
      <div style={{ background: 'rgba(15, 23, 42, 0.9)', border: '1px solid var(--accent-blue)', borderRadius: 'var(--radius-md)', padding: '18px' }}>
        <div style={{ fontSize: '15px', fontWeight: 800, color: '#ffffff', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          <FileText size={18} color="var(--accent-blue)" />
          <span>STEP 13: CONTENT KEYWORD PLACEMENT & DENSITY WORKSTATION</span>
        </div>
        <div style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: '1.5' }}>
          Instructor Methodology (Lecture 11): Place target keywords in exact strategic positions while maintaining a healthy <strong>~0.5% keyword density</strong> to avoid Google Keyword Stuffing penalties.
        </div>

        {/* CONTROLS BAR */}
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center', marginTop: '14px', background: '#020617', padding: '10px 14px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-color)' }}>
          {/* Page Selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8' }}>Select Page:</label>
            <select
              className="input-field"
              style={{ padding: '6px 10px', fontSize: '12px', background: '#0f172a', color: '#ffffff', fontWeight: 700 }}
              value={selectedPageKey}
              onChange={(e) => setSelectedPageKey(e.target.value)}
            >
              <option value="homepage">Homepage (/)</option>
              {servicePages.map((sp, idx) => (
                <option key={sp.id} value={sp.id}>
                  Service #{idx + 1}: {sp.pageName || `Service Page ${idx + 1}`} ({sp.targetUrl})
                </option>
              ))}
            </select>
          </div>

          {/* Word Count Baseline Selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8' }}>Content Baseline:</label>
            <div style={{ display: 'flex', gap: '4px' }}>
              <button
                onClick={() => setWordCountBaseline(500)}
                style={{
                  background: wordCountBaseline === 500 ? 'var(--accent-blue)' : 'rgba(30, 41, 59, 0.8)',
                  border: wordCountBaseline === 500 ? '1px solid #38bdf8' : '1px solid rgba(255, 255, 255, 0.1)',
                  color: '#ffffff',
                  padding: '4px 10px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                500 Words (Standard)
              </button>
              <button
                onClick={() => setWordCountBaseline(1000)}
                style={{
                  background: wordCountBaseline === 1000 ? 'var(--accent-blue)' : 'rgba(30, 41, 59, 0.8)',
                  border: wordCountBaseline === 1000 ? '1px solid #38bdf8' : '1px solid rgba(255, 255, 255, 0.1)',
                  color: '#ffffff',
                  padding: '4px 10px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                1,000 Words (Extended)
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 2. PLACEMENT FORMULA CHECKLIST & AUDIT GRID */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '16px' }}>
        
        {/* 🥇 PRIMARY KEYWORD RULE CARD */}
        <div style={{ background: 'rgba(234, 179, 8, 0.1)', border: '1px solid #facc15', borderRadius: 'var(--radius-md)', padding: '14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: 800, color: '#facc15' }}>🥇 PRIMARY KEYWORD PLACEMENT ({primaryTargetCount}x Target)</span>
            <Badge variant={primaryCount >= primaryTargetCount ? 'complete' : 'tool'}>
              {primaryCount} / {primaryTargetCount} Placed
            </Badge>
          </div>
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#ffffff', marginBottom: '8px' }}>
            "{activePrimary1}" {activePrimary2 ? <span style={{ color: '#facc15', fontSize: '11px' }}>| Var: "{activePrimary2}"</span> : ''}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '11px', color: '#cbd5e1' }}>
            <div style={{ background: 'rgba(15, 23, 42, 0.6)', padding: '6px 8px', borderRadius: '4px', borderLeft: '3px solid #facc15' }}>
              • <strong>Placement 1 (Intro Hook):</strong> Place 1x in intro paragraph.
            </div>
            <div style={{ background: 'rgba(15, 23, 42, 0.6)', padding: '6px 8px', borderRadius: '4px', borderLeft: '3px solid #facc15' }}>
              • <strong>Placement 2 (Middle Body / H2):</strong> Place 1x in middle body section.
            </div>
            <div style={{ background: 'rgba(15, 23, 42, 0.6)', padding: '6px 8px', borderRadius: '4px', borderLeft: '3px solid #facc15' }}>
              • <strong>Placement 3 (Ending Note):</strong> Place 1x in conclusion (final 2-3 lines).
            </div>
          </div>
        </div>

        {/* 🥈 SECONDARY KEYWORD RULE CARD */}
        <div style={{ background: 'rgba(52, 211, 153, 0.1)', border: '1px solid #34d399', borderRadius: 'var(--radius-md)', padding: '14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: 800, color: '#34d399' }}>🥈 SECONDARY KEYWORD PLACEMENT ({secondaryTargetCount}x Target)</span>
            <Badge variant={secondaryCount >= secondaryTargetCount ? 'complete' : 'tool'}>
              {secondaryCount} / {secondaryTargetCount} Placed
            </Badge>
          </div>
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#ffffff', marginBottom: '8px' }}>
            "{activeSecondary1}" {activeSecondary2 ? <span style={{ color: '#34d399', fontSize: '11px' }}>| Var: "{activeSecondary2}"</span> : ''}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '11px', color: '#cbd5e1' }}>
            <div style={{ background: 'rgba(15, 23, 42, 0.6)', padding: '6px 8px', borderRadius: '4px', borderLeft: '3px solid #34d399' }}>
              • <strong>Placement 1 (H2 Subheading):</strong> Place 1x in an &lt;h2&gt; tag.
            </div>
            <div style={{ background: 'rgba(15, 23, 42, 0.6)', padding: '6px 8px', borderRadius: '4px', borderLeft: '3px solid #34d399' }}>
              • <strong>Placement 2 (Body Content):</strong> Place 1x naturally in body text.
            </div>
          </div>
        </div>

        {/* 🥉 TERTIARY KEYWORD RULE CARD */}
        <div style={{ background: 'rgba(56, 189, 248, 0.1)', border: '1px solid #38bdf8', borderRadius: 'var(--radius-md)', padding: '14px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: 800, color: '#38bdf8' }}>🥉 TERTIARY KEYWORD PLACEMENT ({tertiaryTargetCount}x Target)</span>
            <Badge variant={tertiaryCount >= tertiaryTargetCount ? 'complete' : 'tool'}>
              {tertiaryCount} / {tertiaryTargetCount} Placed
            </Badge>
          </div>
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#ffffff', marginBottom: '8px' }}>
            "{activeTertiary1}" {activeTertiary2 ? <span style={{ color: '#38bdf8', fontSize: '11px' }}>| Var: "{activeTertiary2}"</span> : ''}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '11px', color: '#cbd5e1' }}>
            <div style={{ background: 'rgba(15, 23, 42, 0.6)', padding: '6px 8px', borderRadius: '4px', borderLeft: '3px solid #38bdf8' }}>
              • <strong>Placement 1 (H3 / Body):</strong> Place 1x in an &lt;h3&gt; tag or body paragraph.
            </div>
          </div>
        </div>
      </div>

      {/* 3. DENSITY COUNTER & CONTENT EDITOR WORKSTATION */}
      <div style={{ background: '#020617', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', padding: '20px' }}>
        
        {/* TOP STATUS BAR */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '10px' }}>
          <div>
            <div style={{ fontSize: '14px', fontWeight: 800, color: '#ffffff', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Gauge size={18} color="var(--accent-blue)" />
              <span>LIVE KEYWORD DENSITY COUNTER & CONTENT AUDITOR</span>
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
              Target: <strong>{wordCountBaseline} Words Baseline</strong> | Healthy Density: <strong>0.3% – 0.8%</strong> (Penalty Risk: &gt;1.5%)
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 700 }}>Total Words: <span style={{ color: '#ffffff', fontWeight: 800 }}>{words} / {wordCountBaseline}</span></div>
              <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 700 }}>Density: <span style={{ color: densityNum > 1.5 ? '#ef4444' : densityNum >= 0.3 ? '#34d399' : '#facc15', fontWeight: 800 }}>{keywordDensity}%</span></div>
            </div>

            <Button
              variant="secondary"
              size="sm"
              style={{ color: '#c084fc', borderColor: '#a855f7', background: 'rgba(168, 85, 247, 0.12)', fontSize: '11px', fontWeight: 700 }}
              onClick={() => setShowAiDrawer(!showAiDrawer)}
            >
              <Sparkles size={13} style={{ marginRight: '4px' }} />
              AI Placement Prompt Generator
            </Button>
          </div>
        </div>

        {/* KEYWORD DENSITY WARNING GUARD */}
        {densityNum > 1.5 && (
          <div style={{ background: 'rgba(239, 68, 68, 0.15)', border: '1px solid #ef4444', borderRadius: 'var(--radius-sm)', padding: '10px 14px', marginBottom: '14px', color: '#fca5a5', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <AlertTriangle size={16} color="#ef4444" />
            <span><strong>⚠️ WARNING: HIGH KEYWORD STUFFING RISK!</strong> Keyword density is {keywordDensity}% (&gt;1.5%). Reduce repeated keyword usage to avoid Google penalty.</span>
          </div>
        )}

        {densityNum >= 0.3 && densityNum <= 1.0 && words > 100 && (
          <div style={{ background: 'rgba(52, 211, 153, 0.12)', border: '1px solid #34d399', borderRadius: 'var(--radius-sm)', padding: '10px 14px', marginBottom: '14px', color: '#6ee7b7', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ShieldCheck size={16} color="#34d399" />
            <span><strong>✓ HEALTHY KEYWORD DENSITY:</strong> Keyword density is {keywordDensity}% (~0.5% ideal). Content is natural and safe from penalties.</span>
          </div>
        )}

        {/* AI PROMPT DRAWER */}
        {showAiDrawer && (
          <div style={{ background: '#090d16', border: '1px solid rgba(168, 85, 247, 0.4)', borderRadius: 'var(--radius-md)', padding: '16px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <div style={{ fontSize: '12px', fontWeight: 800, color: '#c084fc', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Bot size={16} />
                <span>AI CONTENT GENERATION PROMPT (WITH EXACT PLACEMENT RULES & DENSITY GUARD)</span>
              </div>
              <Button
                variant="secondary"
                size="sm"
                style={{ fontSize: '11px', background: copiedPromptId === selectedPageKey ? '#166534' : 'rgba(168, 85, 247, 0.2)', borderColor: '#a855f7', color: '#fff' }}
                onClick={handleCopyPrompt}
              >
                {copiedPromptId === selectedPageKey ? <Check size={12} style={{ marginRight: '4px' }} /> : <Copy size={12} style={{ marginRight: '4px' }} />}
                {copiedPromptId === selectedPageKey ? 'Copied Prompt!' : 'Copy AI Prompt'}
              </Button>
            </div>
            <textarea
              readOnly
              rows={8}
              className="input-field"
              style={{ fontSize: '11px', fontFamily: 'monospace', color: '#94a3b8', background: '#020617', width: '100%', lineHeight: '1.4' }}
              value={buildAiContentPrompt()}
            />
          </div>
        )}

        {/* CONTENT DRAFT TEXTAREA */}
        <div>
          <label style={{ fontSize: '11px', fontWeight: 700, color: '#e2e8f0', marginBottom: '6px', display: 'block' }}>
            Page Content Draft (Paste AI Output or Type Below):
          </label>
          <textarea
            className="input-field"
            rows={12}
            style={{ width: '100%', fontSize: '12px', color: '#ffffff', lineHeight: '1.6', background: 'rgba(15, 23, 42, 0.8)', padding: '12px' }}
            placeholder={`Draft or paste the ${wordCountBaseline}-word page content here to calculate real-time keyword placement and density...`}
            value={currentContent}
            onChange={(e) => {
              setDraftContentMap({
                ...draftContentMap,
                [selectedPageKey]: e.target.value
              });
            }}
          />
        </div>
      </div>
    </div>
  );
};
